import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { getActionsForPlay, Play, useCreateAdHocPlay } from "@/queries/plays";
import { useReportContext } from "@/stores/ReportContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Copy } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  AdHocPlayForm,
  AdHocPlayFormValues,
  adHocPlaySchema,
} from "./AdHocPlayForm";

interface DuplicateAdHocPlayButtonProps {
  play: Play;
}

export function DuplicateAdHocPlayButton({
  play,
}: DuplicateAdHocPlayButtonProps) {
  const { reportId } = useReportContext();
  const { mutate: createPlay, isPending } = useCreateAdHocPlay(reportId);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);

  // Fetch actions for this play when dialog is open
  const { data: actionsData } = getActionsForPlay(play.id);

  const form = useForm<AdHocPlayFormValues>({
    resolver: zodResolver(adHocPlaySchema),
    defaultValues: {
      title: "",
      category: "General",
      location_type: "gate",
      location_details: "",
      terminal: null,
      concourse: null,
      start_interval: 0,
      end_interval: 0,
      actions: [{ title: "", team_id: null, order: 0 }],
    },
    mode: "onBlur",
  });

  // Pre-fill form when the dialog opens or the play data changes
  useEffect(() => {
    if (isOpen && play) {
      // Prepare actions data - use fetched actions or default to one empty action
      const actionsForForm =
        actionsData && actionsData.length > 0
          ? actionsData.map((action, index) => ({
              title: action.message || "",
              team_id: action.team?.id || null,
              order: action.order !== undefined ? action.order : index,
            }))
          : [{ title: "", team_id: null, order: 0 }];

      form.reset({
        title: `${play.title} (Copy)`,
        category: play.category || "General",
        location_type: play.location_type || "gate",
        location_details: play.location_details || "",
        terminal: play.terminal || null,
        concourse: play.concourse || null,
        start_interval: play.start_interval || 0,
        end_interval: play.end_interval || 0,
        actions: actionsForForm,
      });
    } else if (!isOpen) {
      form.reset(); // Clear form when closing
    }
  }, [isOpen, play, actionsData, form.reset]);

  const onSubmit = async (data: AdHocPlayFormValues) => {
    try {
      const filteredActions = data.actions
        .filter((action) => action.title.trim() !== "")
        .map((action, index) => ({
          title: action.title,
          team_id: action.team_id,
          order: action.order !== undefined ? action.order : index,
        }));

      const playData = {
        title: data.title,
        category: data.category,
        location_type: data.location_type,
        location_details: data.location_details || "",
        terminal: data.terminal || "",
        concourse: data.concourse || "",
        start_interval: data.start_interval,
        end_interval: data.end_interval,
        actions: filteredActions,
      };

      createPlay(playData, {
        onSuccess: () => {
          toast({
            title: "Success",
            description: "Play duplicated successfully.",
          });
          queryClient.invalidateQueries({ queryKey: ["plays"] });
          form.reset();
          setIsOpen(false);
        },
        onError: (error) => {
          toast({
            variant: "destructive",
            title: "Error duplicating play",
            description: error.message,
          });
        },
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error duplicating play",
        description: "An unexpected error occurred.",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon">
          <Copy className="h-4 w-4" />
          <span className="sr-only">Duplicate Play</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[1000px] bg-slate-900 border-slate-700 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-slate-100">
            Duplicate One-Time Play
          </DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <AdHocPlayForm
            form={form}
            onSubmit={onSubmit}
            isLoading={isPending}
            submitButtonText="Create Duplicate"
            onClose={() => setIsOpen(false)}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
